/**
 * The sensitivity loop and its tornado (PRD 10.9; Design Review V-12; TRD 10.2
 * `GET /forecast/sensitivity`; Phases 46 and 47), shared by the read model behind the endpoint,
 * the browser card and the CSV export.
 *
 * The question
 * ------------
 * **Which single deal or assumption moves the peak the most?** Scenario planning as specified
 * asks a planner to build a what-if before they can know which one is worth building. This is the
 * other order: pull every lever the tool already knows about, once, against one run, and rank
 * them by how far each moves the peak week. Then build the one scenario that mattered.
 *
 * What a lever is worth
 * ---------------------
 * The peak the run produces with that lever pulled, minus the peak it produces as it stands. The
 * peak is the company's worst week over the run's horizon, picked exactly as the dashboard picks
 * it (`pickPeak` below is the one definition, and the bridge read uses it too), so a tornado's
 * base line is the number on the KPI tile and not a second opinion about it.
 *
 * Why the peaks can be computed rather than re-run
 * ------------------------------------------------
 * Removing a deal from the pipeline removes that deal's allocation rows and changes nothing else
 * — no other deal grows, no capacity moves — so the peak without it is the peak over the same
 * weekly series less those rows. That is arithmetic over immutable rows, it is exactly what a
 * Phase 35 scenario excluding that deal recomputes, and it is why the answer can be cached.
 *
 * The exception is the capacity levers: what a freelancer adds is the Phase 7 model's answer, not
 * the run's, so it is taken as the difference between the model with that source and without it
 * and added to the run's frozen line. A lever that says so is honest; one that re-derived weekly
 * hours here would be a second capacity model.
 *
 * Pure: no DOM, no I/O.
 */
import { col } from './table.js';

/** The kinds of lever, in the order a reader meets them. */
export const LEVER_KINDS = Object.freeze(['base', 'deal', 'stage', 'pipeline', 'elastic']);

/** What pulling a lever does. `base` is the run as it stands. */
export const LEVER_DIRECTIONS = Object.freeze(['base', 'remove', 'add', 'swap']);

/** What a tornado bar's length may stand for. The plan's Delivers ranks on the peak. */
export const SENSITIVITY_MEASURES = Object.freeze(['utilization', 'over_hours']);

/** How the levers may be ordered. `span` is V-12's own: the biggest mover first. */
export const SENSITIVITY_ORDERS = Object.freeze(['span', 'hours', 'name', 'kind']);

/** What each kind is called on screen, singular and as a group. */
export const LEVER_KIND_LABELS = Object.freeze({
  base: 'Base',
  deal: 'Deals',
  stage: 'Stages',
  pipeline: 'The pipeline',
  elastic: 'Elastic capacity',
});

/** One line per kind saying what pulling it means, for the legend and the tooltip. */
export const LEVER_KIND_NOTES = Object.freeze({
  base: 'the run as it stands',
  deal: 'this deal is lost, or was never real',
  stage: 'every open deal in this stage goes away',
  pipeline: 'the whole pipeline layer read another way',
  elastic: 'this freelancer or contractor is booked',
});

export const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
/** Two decimals of an SVG coordinate: enough for a pixel, short enough to read in a test. */
const r = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
export const round4 = (n) => Math.round((n + Number.EPSILON) * 10000) / 10000;
const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

/** Utilization of hours against a denominator; null with no capacity (the engine's rule). */
export function utilizationOf(hours, capacityHours) {
  const cap = Number(capacityHours) || 0;
  return cap > 0 ? round4(Number(hours) / cap) : null;
}

/**
 * The peak of a weekly series: the week with the highest utilization, ties to the week with more
 * hours, and a week with neither demand nor capacity is not a candidate at all.
 *
 * This is the one definition of "the peak week" in the tool. The bridge read (and so the
 * dashboard's KPI tile and the summary) picks its peak with this function, and so does every
 * lever here — which is what makes the tornado's base line the number on the tile rather than a
 * near-miss that has to be explained.
 *
 * @param {{ week_start: string, demand_hours: number, capacity_hours: number }[]} weeks
 */
export function pickPeak(weeks) {
  let peak = null;
  for (const w of weeks ?? []) {
    const demand = Number(w.demand_hours) || 0;
    const capacity = Number(w.capacity_hours) || 0;
    if (!(demand > 0 || capacity > 0)) continue;
    const u = utilizationOf(demand, capacity);
    const beats =
      peak === null ||
      (u ?? -1) > (peak.utilization ?? -1) ||
      ((u ?? -1) === (peak.utilization ?? -1) && demand > peak.demand_hours);
    if (!beats) continue;
    peak = {
      week_start: w.week_start,
      demand_hours: round2(demand),
      capacity_hours: round2(capacity),
      headroom_hours: round2(capacity - demand),
      over_hours: round2(Math.max(0, demand - capacity)),
      utilization: u,
    };
  }
  return peak;
}

/** How many weeks of a series stand above their own capacity. */
export function weeksOver(weeks) {
  return (weeks ?? []).filter(
    (w) => (Number(w.capacity_hours) || 0) > 0 && Number(w.demand_hours) > w.capacity_hours,
  ).length;
}

/** A lever's value under the reader's choice of what a bar's length means. */
export function leverValue(lever, measure = 'utilization') {
  if (measure === 'over_hours') return num(lever?.peak_over_hours) ?? 0;
  return num(lever?.peak_utilization);
}

/** How far this lever moved the base, in the chosen measure. Negative relieves the peak. */
export function leverDelta(lever, measure = 'utilization') {
  if (measure === 'over_hours') return num(lever?.delta_over_hours) ?? 0;
  return num(lever?.delta_utilization) ?? 0;
}

// ---------------------------------------------------------------------------------------------
// Model

const matches = (lever, needle) =>
  [lever.label, lever.lever_key, lever.detail?.client]
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(needle));

const orderComparator = (order, measure) => {
  const byName = (a, b) =>
    String(a.label).localeCompare(String(b.label)) || a.key.localeCompare(b.key);
  if (order === 'name') return byName;
  if (order === 'kind')
    return (a, b) =>
      LEVER_KINDS.indexOf(a.lever_kind) - LEVER_KINDS.indexOf(b.lever_kind) ||
      Math.abs(leverDelta(b, measure)) - Math.abs(leverDelta(a, measure)) ||
      byName(a, b);
  if (order === 'hours')
    return (a, b) => Math.abs(b.hours_moved) - Math.abs(a.hours_moved) || byName(a, b);
  // V-12's own: sorted by span, which is the size of the move and not its direction.
  return (a, b) =>
    Math.abs(leverDelta(b, measure)) - Math.abs(leverDelta(a, measure)) ||
    Math.abs(b.hours_moved) - Math.abs(a.hours_moved) ||
    byName(a, b);
};

/**
 * Everything the tornado draws, from a `GET /forecast/sensitivity` response.
 *
 * The controls hide levers in the browser, never on the server: a lever's own delta is about the
 * run and must not follow what is on screen. What is hidden is counted and stated.
 *
 * @param {object} data  a `GET /forecast/sensitivity` response
 * @param {{ measure?: 'utilization'|'over_hours', order?: string, kinds?: string[],
 *           minDelta?: number, q?: string }} [opts]
 */
export function sensitivityModel(
  data,
  { measure = 'utilization', order = 'span', kinds = null, minDelta = 0, q = '' } = {},
) {
  if (!data?.base) return null;
  const wanted = new Set(kinds?.length ? kinds : LEVER_KINDS.filter((k) => k !== 'base'));
  const needle = String(q ?? '')
    .trim()
    .toLowerCase();
  const floor = Number(minDelta) > 0 ? Number(minDelta) : 0;

  const base = {
    ...data.base,
    value: leverValue(data.base, measure),
  };
  const all = (data.levers ?? []).map((l) => ({
    ...l,
    value: leverValue(l, measure),
    delta: leverDelta(l, measure),
    span: Math.abs(leverDelta(l, measure)),
    // A lever that pulls the peak down relieves; one that pushes it up is a risk the planner is
    // carrying without having decided to. Both are worth a bar, and they point opposite ways.
    relieves: leverDelta(l, measure) < 0,
  }));

  // `all` is sorted too, so the table below the chart reads in the same order as the bars and
  // carries every lever whatever the controls are hiding.
  all.sort(orderComparator(order, measure));
  const shown = all
    .filter((l) => wanted.has(l.lever_kind))
    .filter((l) => l.span >= floor)
    .filter((l) => !needle || matches(l, needle));

  const perKind = Object.fromEntries(LEVER_KINDS.map((k) => [k, 0]));
  for (const l of all) perKind[l.lever_kind] = (perKind[l.lever_kind] ?? 0) + 1;

  return {
    run_id: data.run_id ?? null,
    from: data.from,
    to: data.to,
    weeks_count: data.weeks_count ?? 0,
    pipeline_mode: data.pipeline_mode ?? 'weighted',
    thresholds: data.thresholds ?? null,
    measure,
    order,
    kinds: [...wanted],
    min_delta: floor,
    base,
    levers: shown,
    all,
    per_kind: perKind,
    hidden: all.length - shown.length,
    cached_at: data.cached_at ?? null,
  };
}

/** The figures the card's header and notes state, counted over what is drawn. */
export function sensitivitySummary(model) {
  const levers = model?.levers ?? [];
  const relieving = levers.filter((l) => l.relieves);
  const worsening = levers.filter((l) => !l.relieves && l.span > 0);
  return {
    levers: levers.length,
    hidden: model?.hidden ?? 0,
    per_kind: model?.per_kind ?? {},
    biggest: levers.reduce((best, l) => (best === null || l.span > best.span ? l : best), null),
    biggest_relief: relieving.reduce(
      (best, l) => (best === null || l.span > best.span ? l : best),
      null,
    ),
    biggest_risk: worsening.reduce(
      (best, l) => (best === null || l.span > best.span ? l : best),
      null,
    ),
    // How much of the peak one lever can take off: the honest answer to "can we fix this by
    // dropping something?" — and it is usually no, which is the point of the view.
    flat: levers.filter((l) => l.span === 0).length,
    base_peak: model?.base ?? null,
  };
}

// ---------------------------------------------------------------------------------------------
// The tornado (Phase 47, V-12)

/**
 * The chart's scale: the span of values the bars have to fit, always including the base line.
 *
 * A tornado is read against its centre, so the axis is chosen to keep the base line *on* the
 * chart and every bar's end inside it. The domain is padded outward to a round-ish figure so the
 * axis ticks land somewhere a reader can say out loud, and it is never narrower than the base
 * itself — a chart whose centre line sat outside its own axis would be a drawing error dressed
 * as a scale.
 *
 * @param {ReturnType<typeof sensitivityModel>} model
 * @param {{ pad?: number }} [opts]
 */
export function tornadoDomain(model, { pad = 0.04 } = {}) {
  const base = Number(model?.base?.value) || 0;
  const values = (model?.levers ?? []).map((l) => Number(l.value) || 0);
  const lo = Math.min(base, ...values, base);
  const hi = Math.max(base, ...values, base);
  const span = Math.max(hi - lo, Math.abs(base) * pad, pad);
  return { min: lo - span * pad, max: hi + span * pad, base };
}

/**
 * One bar per lever, centred on the base line: the bar runs from the base to the peak this lever
 * would produce, so its length is the move and its side is the direction — left relieves, right
 * makes things worse.
 *
 * Bars are laid out in the model's order, which is the reader's chosen sort and defaults to
 * V-12's own (by span, biggest first). A lever that moves nothing is still a row with a label and
 * a value: it is an answer, and the most common one, and a chart that dropped it would suggest
 * the tool had not considered it.
 *
 * @param {ReturnType<typeof sensitivityModel>} model
 * @param {{ width?: number, rowHeight?: number, barHeight?: number, top?: number,
 *           domain?: { min: number, max: number } }} [opts]
 */
export function tornadoLayout(
  model,
  { width = 520, rowHeight = 26, barHeight = 14, top = 0, domain = null } = {},
) {
  const levers = model?.levers ?? [];
  const scale = domain ?? tornadoDomain(model);
  const range = scale.max - scale.min;
  const x = (value) => (range > 0 ? ((Number(value) - scale.min) / range) * width : width / 2);
  const baseX = r(x(scale.base));

  const bars = levers.map((lever, i) => {
    const end = x(lever.value);
    const y = top + i * rowHeight;
    const from = Math.min(baseX, end);
    const to = Math.max(baseX, end);
    return {
      key: lever.key,
      lever,
      index: i,
      y,
      top: y + (rowHeight - barHeight) / 2,
      height: barHeight,
      x: r(from),
      // A bar of no width is still drawn, as a hairline on the base: "this lever does nothing"
      // is a finding, and an invisible bar would read as a missing row.
      width: r(Math.max(to - from, 0.75)),
      end: r(end),
      relieves: lever.relieves,
      centre: y + rowHeight / 2,
    };
  });

  return {
    bars,
    baseX,
    width,
    height: Math.max(top, top + levers.length * rowHeight),
    domain: scale,
    scale: x,
  };
}

/**
 * Axis ticks across the domain, at a step a reader can say: whole percentage points where the
 * span is small, fives or tens where it is wide.
 * @param {{ min: number, max: number }} domain
 * @param {{ count?: number, percent?: boolean }} [opts]
 */
export function tornadoTicks(domain, { count = 5, percent = true } = {}) {
  const span = domain.max - domain.min;
  if (!(span > 0)) return [domain.min];
  const raw = span / Math.max(2, count);
  const unit = percent ? 0.01 : 1;
  const steps = percent ? [1, 2, 5, 10, 25, 50] : [1, 5, 10, 25, 50, 100, 250, 500];
  const step = (steps.find((s) => s * unit >= raw) ?? steps.at(-1)) * unit;
  const first = Math.ceil(domain.min / step) * step;
  const out = [];
  for (let v = first; v <= domain.max + step / 1000; v += step) out.push(round4(v));
  return out;
}

// ---------------------------------------------------------------------------------------------
// Table columns (Phase 37)

/** One row per lever: the tornado's own table. */
export function sensitivityColumns({ measure = 'utilization' } = {}) {
  return [
    col.text('lever', 'Lever'),
    col.text('kind', 'Kind'),
    col.text('direction', 'Pull'),
    col.week('peak_week', 'Peak week'),
    col.percent('peak_utilization', 'Peak'),
    col.hours('peak_over_hours', 'Over at the peak'),
    measure === 'over_hours'
      ? col.hours('delta_over_hours', 'Change in hours over')
      : col.percent('delta_utilization', 'Change in peak'),
    col.hours('hours_moved', 'Hours moved'),
    col.hours('hours_moved_peak_week', 'In the base peak week'),
    col.count('weeks_over', 'Weeks over'),
    col.text('note', 'What it is'),
  ];
}

/**
 * One row per lever, base first, in the order the chart draws them — and every lever the response
 * carries, not only the ones the controls left on screen. A reader who has hidden a kind has hidden
 * it from the picture, not from the evidence.
 */
export function sensitivityRows(model) {
  const rows = [];
  if (model?.base)
    rows.push({
      lever: model.base.label,
      kind: 'base',
      direction: 'base',
      peak_week: model.base.peak_week_start,
      peak_utilization: num(model.base.peak_utilization),
      peak_over_hours: num(model.base.peak_over_hours) ?? 0,
      delta_utilization: 0,
      delta_over_hours: 0,
      hours_moved: 0,
      hours_moved_peak_week: 0,
      weeks_over: model.base.weeks_over ?? 0,
      note: LEVER_KIND_NOTES.base,
    });
  for (const l of model?.all ?? model?.levers ?? [])
    rows.push({
      lever: l.label,
      kind: l.lever_kind,
      direction: l.direction,
      peak_week: l.peak_week_start,
      peak_utilization: num(l.peak_utilization),
      peak_over_hours: num(l.peak_over_hours) ?? 0,
      delta_utilization: num(l.delta_utilization) ?? 0,
      delta_over_hours: num(l.delta_over_hours) ?? 0,
      hours_moved: num(l.hours_moved) ?? 0,
      hours_moved_peak_week: num(l.hours_moved_peak_week) ?? 0,
      weeks_over: l.weeks_over ?? 0,
      note: LEVER_KIND_NOTES[l.lever_kind] ?? '',
    });
  return rows;
}
